import React, { useMemo, useState } from 'react';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import {
  ESCALATION_DONTS,
  ESCALATION_DOS,
  FUNCTIONS_OF_BEHAVIOR,
  MTSS_TIERS,
  PRAISE_PHRASES,
} from '../../lib/pbisConstants';

// External resources — publicly-funded, freely-accessible training modules
// and tool libraries from established U.S. education research centers.
// Descriptions are short and factual so the page reads as a reference,
// not marketing copy. Each entry credits its provider explicitly.
const EXTERNAL_RESOURCES = [
  {
    title: 'NCII — Behavior Support for Intensive Intervention',
    url: 'https://intensiveintervention.org/training/course-content/behavior-support-intensive-intervention',
    provider: 'National Center on Intensive Intervention',
    description:
      'Free training modules on FBA, BIP, and data-based individualization for Tier 3 students.',
    tags: ['Tier 3', 'FBA', 'BIP', 'Training'],
  },
  {
    title: 'AFIRM — Autism Focused Intervention Resource Modules',
    url: 'https://afirm.fpg.unc.edu/afirm-modules/',
    provider: 'UNC Frank Porter Graham Child Development Institute',
    description:
      'Modules on 28 evidence-based practices for learners on the autism spectrum, including reinforcement, prompting, and visual supports.',
    tags: ['Autism', 'EBPs'],
  },
  {
    title: 'IRIS — Functional Behavioral Assessment (Elementary)',
    url: 'https://iris.peabody.vanderbilt.edu/product/fba-elem/',
    provider: 'Vanderbilt Peabody · IRIS Center',
    description:
      'Step-by-step FBA module with case studies and printable tools.',
    tags: ['FBA', 'Elementary', 'IRIS'],
  },
  {
    title: 'IRIS — Early Childhood Behavior Management (ECBM)',
    url: 'https://iris.peabody.vanderbilt.edu/product/ecbm/',
    provider: 'Vanderbilt Peabody · IRIS Center',
    description:
      'Positive strategies for early-childhood classrooms — schedules, transitions, and reinforcement.',
    tags: ['Early Childhood', 'Tier 1'],
  },
  {
    title: 'IRIS — Behavior & Classroom Management (BP)',
    url: 'https://iris.peabody.vanderbilt.edu/product/bp/',
    provider: 'Vanderbilt Peabody · IRIS Center',
    description:
      'Classroom-level behavior plans and universal supports; the "Tier 1" companion to FBA.',
    tags: ['Tier 1', 'Classroom'],
  },
  {
    title: 'Intervention Central — Behavioral Interventions',
    url: 'https://www.interventioncentral.org/behavioral-intervention-modification',
    provider: 'Intervention Central',
    description:
      'Publicly available intervention library and printable tools (check-in / check-out, behavior contracts, reinforcement menus).',
    tags: ['Interventions', 'CICO', 'Contracts'],
  },
  {
    title: 'CEC — Teacher Resources',
    url: 'https://exceptionalchildren.org/teacher-resources',
    provider: 'Council for Exceptional Children',
    description:
      'Practice guides and toolkits for special educators, including behavior and social-emotional supports.',
    tags: ['SPED', 'Practice Guides'],
  },
];

const PBISResourcesPage = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [praiseBehavior, setPraiseBehavior] = useState('');
  const [phraseIndex, setPhraseIndex] = useState(0);

  const currentPhrase = PRAISE_PHRASES[phraseIndex % PRAISE_PHRASES.length];
  const behaviorFragment = praiseBehavior.trim();
  const praisePreview = useMemo(() => {
    if (!behaviorFragment) return currentPhrase;
    // The point of the generator: pair any praise opener with the specific
    // observed behavior — that's what turns generic praise into
    // "behavior-specific praise," a Tier 1 evidence-based practice.
    return `${currentPhrase} ${behaviorFragment}.`;
  }, [currentPhrase, behaviorFragment]);

  const nextPhrase = () => setPhraseIndex((idx) => (idx + 1) % PRAISE_PHRASES.length);

  return (
    <div className="min-h-screen bg-background">
      <Header
        onMenuToggle={() => setIsSidebarOpen(!isSidebarOpen)}
        isMenuOpen={isSidebarOpen}
      />
      <Sidebar
        isOpen={isSidebarOpen}
        onClose={() => setIsSidebarOpen(false)}
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
      />
      <main
        className={`transition-all duration-300 ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-72'
        } pt-16`}
      >
        <div className="p-6 max-w-6xl mx-auto space-y-8">
          <Breadcrumb
            customItems={[
              { label: 'Dashboard', path: '/dashboard', icon: 'LayoutDashboard' },
              { label: 'Behavior Toolkit', path: null, icon: 'BookOpen', current: true },
            ]}
          />

          <div>
            <h1 className="text-3xl font-bold text-foreground">Behavior Toolkit</h1>
            <p className="text-muted-foreground mt-2 max-w-3xl">
              Reference cards to help you support students&apos; behavior — the levels of
              support most schools use, the four common reasons behavior happens, what to
              do (and not do) when a student is escalating, praise phrases you can pair
              with what a student did well, and free training modules if you want to learn
              more.
            </p>
            {/* Compact acronym legend — hidden behind a summary so it doesn't
                dominate the top of the page. New teachers can peek if they
                need to; experienced staff can ignore it. */}
            <details className="mt-3 max-w-3xl">
              <summary className="text-xs text-muted-foreground cursor-pointer hover:text-primary select-none">
                What do PBIS, MTSS, FBA, and BIP stand for?
              </summary>
              <p className="text-xs text-muted-foreground mt-2">
                <strong>PBIS</strong> (Positive Behavioral Interventions and Supports) —
                the school-wide approach used on most U.S. campuses (
                <a
                  href="https://www.pbis.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-primary"
                >
                  pbis.org
                </a>
                ).{' '}
                <strong>MTSS</strong> (Multi-Tiered System of Supports) — the three-level
                pyramid below (all students → some students → few students).{' '}
                <strong>FBA</strong> (Functional Behavior Assessment) — figuring out
                <em> why </em>a behavior happens.{' '}
                <strong>BIP</strong> (Behavior Intervention Plan) — the written plan for
                what the team will do about it. Nothing on this page reproduces content
                from any proprietary training curriculum.
              </p>
            </details>
          </div>

          {/* MTSS pyramid ---------------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Triangle" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">The three levels of behavior support</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Every student gets Tier 1 (the school-wide expectations). About 15 out of
              every 100 students also need Tier 2 (small-group supports). About 5 out of
              every 100 need Tier 3 (an individualized plan built around why the behavior
              is happening).
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {MTSS_TIERS.slice()
                .reverse()
                .map((tier) => (
                  <div
                    key={tier.value}
                    className="rounded-lg border p-4"
                    style={{ borderColor: `${tier.color}55`, backgroundColor: `${tier.color}0d` }}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <span
                        className="w-8 h-8 rounded-full flex items-center justify-center text-white font-bold"
                        style={{ backgroundColor: tier.color }}
                      >
                        {tier.value}
                      </span>
                      <p className="font-semibold text-card-foreground">{tier.label}</p>
                    </div>
                    <p className="text-sm text-muted-foreground">{tier.description}</p>
                    <ul className="text-xs mt-3 space-y-1 list-disc list-inside">
                      {tier.value === 1 && (
                        <>
                          <li>Clearly taught school-wide expectations</li>
                          <li>4:1 positive-to-corrective ratio</li>
                          <li>Predictable routines &amp; visual schedules</li>
                        </>
                      )}
                      {tier.value === 2 && (
                        <>
                          <li>Check-In / Check-Out (CICO)</li>
                          <li>Small-group social skills groups</li>
                          <li>Behavior contracts &amp; token economies</li>
                        </>
                      )}
                      {tier.value === 3 && (
                        <>
                          <li>Full FBA + individualized BIP</li>
                          <li>Wrap-around team &amp; family involvement</li>
                          <li>Daily data collection &amp; review cycles</li>
                        </>
                      )}
                    </ul>
                  </div>
                ))}
            </div>
          </section>

          {/* Four functions ------------------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="Brain" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">Functions of Behavior</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              Behavior is communication. Every persistent behavior is trying to get or avoid something. Identify the function first,
              then teach a replacement behavior that serves the same function more efficiently.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {FUNCTIONS_OF_BEHAVIOR.map((fn) => (
                <div
                  key={fn.value}
                  className="rounded-lg border p-4"
                  style={{ borderColor: `${fn.color}55`, backgroundColor: `${fn.color}0d` }}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <Icon name={fn.icon} size={18} style={{ color: fn.color }} />
                    <p className="font-semibold text-card-foreground">{fn.label}</p>
                  </div>
                  <p className="text-sm text-muted-foreground">{fn.description}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Escalation dos & don'ts ---------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="AlertTriangle" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">Responding to Escalation</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-6">
              During escalation, staff behavior can either fuel or interrupt the cycle. Fewer words, calmer tone, physical safety first.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-lg border border-success/30 bg-success/5 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="ThumbsUp" size={18} className="text-success" />
                  <p className="font-semibold text-success">Do</p>
                </div>
                <ul className="text-sm space-y-2">
                  {ESCALATION_DOS.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Icon name="Check" size={14} className="text-success mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="ThumbsDown" size={18} className="text-destructive" />
                  <p className="font-semibold text-destructive">Don't</p>
                </div>
                <ul className="text-sm space-y-2">
                  {ESCALATION_DONTS.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Icon name="X" size={14} className="text-destructive mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          {/* Behavior-specific praise generator ----------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="MessageCircle" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">Behavior-Specific Praise Bank</h2>
            </div>
            <p className="text-sm text-muted-foreground mb-4">
              Pair a common praise opener with what the student actually did — that turns generic
              praise into <span className="italic">behavior-specific praise</span>, which is a Tier 1
              evidence-based practice. The phrases below are everyday English openers; no specific
              published praise-phrase compilation is reproduced here.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="md:col-span-2 bg-primary/5 rounded-lg p-5 border border-primary/20">
                <p className="text-xs uppercase text-primary font-medium tracking-wide mb-2">Praise preview</p>
                <p className="text-lg font-medium text-foreground min-h-[3rem]">
                  “{praisePreview}”
                </p>
                <div className="mt-4">
                  <Input
                    label="What did the student do?"
                    placeholder='e.g. "waiting your turn quietly"'
                    value={praiseBehavior}
                    onChange={(e) => setPraiseBehavior(e.target.value)}
                  />
                </div>
                <div className="flex flex-wrap gap-2 mt-4">
                  <Button variant="outline" size="sm" onClick={nextPhrase}>
                    <Icon name="Shuffle" size={14} />
                    Try another phrase
                  </Button>
                  {behaviorFragment && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        if (navigator?.clipboard?.writeText) {
                          navigator.clipboard.writeText(praisePreview).catch(() => {});
                        }
                      }}
                    >
                      <Icon name="Copy" size={14} />
                      Copy phrase
                    </Button>
                  )}
                </div>
              </div>
              <div className="bg-muted/40 rounded-lg p-5 border border-border">
                <p className="text-xs uppercase text-muted-foreground font-medium tracking-wide mb-2">
                  All phrases
                </p>
                <div className="flex flex-wrap gap-1.5 max-h-56 overflow-auto pr-1">
                  {PRAISE_PHRASES.map((phrase, idx) => (
                    <button
                      key={phrase}
                      type="button"
                      onClick={() => setPhraseIndex(idx)}
                      className={`px-2 py-1 rounded text-xs border transition-colors ${
                        idx === phraseIndex % PRAISE_PHRASES.length
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-foreground border-border hover:border-primary'
                      }`}
                    >
                      {phrase}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* Vetted external resources -------------------------------- */}
          <section className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center gap-2 mb-4">
              <Icon name="BookOpen" size={20} className="text-primary" />
              <h2 className="text-xl font-semibold text-card-foreground">External Training &amp; Tools</h2>
            </div>

            <div className="mb-4 p-4 rounded-md bg-warning/10 border border-warning/30 text-sm">
              <div className="flex items-start gap-2">
                <Icon name="Info" size={18} className="text-warning flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-foreground">Not affiliated. Not endorsed. Not a partnership.</p>
                  <p className="text-muted-foreground mt-1">
                    EduTracker Pro is not affiliated with, endorsed by, or partnered with any of the
                    organizations linked below. All names, materials, and content on the linked sites
                    remain the property of their respective owners. These links are provided solely as
                    a convenience to point educators toward publicly available reference material —
                    each link opens the provider's own site in a new tab. If any listed organization
                    would like their link removed, please contact us and we will do so promptly.
                  </p>
                </div>
              </div>
            </div>

            <p className="text-sm text-muted-foreground mb-6">
              Publicly available, evidence-based modules and tool libraries from established U.S.
              education research centers. Some providers may charge for professional-development
              credit hours on modules that are otherwise free to view — any such fee is between
              you and that provider.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {EXTERNAL_RESOURCES.map((res) => (
                <a
                  key={res.url}
                  href={res.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group rounded-lg border border-border p-4 hover:border-primary hover:bg-primary/5 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-card-foreground group-hover:text-primary">
                        {res.title}
                      </p>
                      <p className="text-xs text-muted-foreground mt-0.5">{res.provider}</p>
                    </div>
                    <Icon
                      name="ExternalLink"
                      size={16}
                      className="text-muted-foreground group-hover:text-primary flex-shrink-0"
                    />
                  </div>
                  <p className="text-sm text-muted-foreground mt-2">{res.description}</p>
                  <div className="flex flex-wrap gap-1 mt-3">
                    {res.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 rounded-full text-xs bg-muted text-muted-foreground border border-border"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </a>
              ))}
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default PBISResourcesPage;
